/**
 * The sourcing chip that sits under a verdict ("Based on a live web search",
 * "Based on AI knowledge…"), with an info affordance that explains in plain
 * language where the answer came from — a hover title alone was invisible on
 * touch and left "live web search" unexplained.
 */

import { useEffect, useRef, useState } from 'react'
import { SOURCING_EXPLAINER, SOURCING_LABEL, type SourcingKind } from '@treu/shared'

export type { SourcingKind }

const CHIP_CLASS: Record<SourcingKind, string> = {
  web_search: 'bg-accent-100 border-accent-300/50 text-accent-600',
  ai_knowledge: 'bg-verdict-mixed-bg border-verdict-mixed/20 text-verdict-mixed',
}


interface SourcingChipProps {
  kind: SourcingKind
  /** Engine-provided detail on what the score rests on, shown inside the popover. */
  knowledgeBasis?: string
}

export function SourcingChip({ kind, knowledgeBasis }: SourcingChipProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <span ref={wrapRef} className="relative inline-block mt-2 mr-2 align-middle">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-full text-[0.6875rem] ${CHIP_CLASS[kind]}`}
      >
        {SOURCING_LABEL[kind]}
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
          aria-label={`What does “${SOURCING_LABEL[kind]}” mean?`}
          className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border border-current text-[0.5625rem] font-semibold leading-none cursor-pointer bg-transparent p-0 opacity-80 hover:opacity-100"
        >
          i
        </button>
      </span>
      {open && (
        <span
          role="tooltip"
          className="absolute left-0 top-full z-50 mt-1.5 block w-[min(20rem,80vw)] rounded-lg border border-paper-200 bg-white p-3 text-xs leading-relaxed text-ink-700 shadow-overlay"
        >
          {SOURCING_EXPLAINER[kind]}
          {knowledgeBasis && (
            <span className="mt-2 block border-t border-paper-200 pt-2 text-ink-600">{knowledgeBasis}</span>
          )}
        </span>
      )}
    </span>
  )
}

export default SourcingChip
